import React, { useState, useEffect, useRef, useMemo } from "react";
import BreadCrumb from "../component/breadcrumb/Breadcrumb";
import Layout from "../component/Layout";
import GalleryItem from "../component/gallery/GalleryItem";
import GalleryItemVideo from "../component/gallery/GalleryItemVideo";
import { FaSistrix, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { API_URL } from "../api/APIUrl";
import FsLightbox from "fslightbox-react";

const Material = () => {
  const [getAllItems, setItem] = useState([]);
  const [activeFilter, setActiveFilter] = useState("view all");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState("");

  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchdata();
  }, []);

  const fetchdata = async () => {
    try {
      const res = await fetch(`${API_URL}/api/materials`);
      const json = await res.json();
      const materials = Array.isArray(json.data) ? json.data : [];
      setItem(materials);
    } catch (err) {
      console.error("Failed to fetch materials:", err);
      setItem([]);
    }
  };

  const handleFilterChange = (e) => {
    e.preventDefault();
    const targetFilter = e.target.getAttribute("data-filter");
    setCurrentPage(1);
    setActiveFilter(targetFilter);
  };

  const handleSearchChange = (e) => {
    setQuery(e.target.value);
    setCurrentPage(1);
  };

  const processedItems = useMemo(() => {
    let dataset = [...getAllItems];
    if (activeFilter !== "view all") {
      dataset = dataset.filter((item) => item.category === activeFilter);
    }
    if (query.trim() !== "") {
      const searchLower = query.toLowerCase();
      dataset = dataset.filter(
        (item) =>
          item.postDesc?.toLowerCase().includes(searchLower) ||
          item.postTitle?.toLowerCase().includes(searchLower)
      );
    }
    return dataset.reverse();
  }, [getAllItems, activeFilter, query]);

  const finalIndex = currentPage * itemsPerPage;
  const startingIndex = finalIndex - itemsPerPage;
  const items = processedItems.slice(startingIndex, finalIndex);
  const npage = Math.ceil(processedItems.length / itemsPerPage);

  function prePage() {
    if (currentPage !== 1) setCurrentPage(currentPage - 1);
  }
  function changeCPage(id) {
    setCurrentPage(id);
  }
  function nextPage() {
    if (currentPage !== npage) setCurrentPage(currentPage + 1);
  }

  function getPageRange(current, total) {
    const range = [];
    if (total <= 6) {
      for (let i = 1; i <= total; i++) range.push(i);
    } else {
      range.push(1);
      if (current > 4) range.push("...");
      if (current > 3 && current < total - 2) {
        range.push(current - 1, current, current + 1);
      } else if (current <= 3) {
        range.push(2, 3, 4);
      } else {
        range.push(total - 3, total - 2, total - 1);
      }
      if (current < total - 3) range.push("...");
      range.push(total);
    }
    return range;
  }

  const openGlobalPreview = (url) => {
    setLightboxUrl(url);
    setLightboxToggler((prev) => !prev);
  };

  // --- RE-ENGINEERED PAGINATION DESIGN SYSTEM ---
  const paginationButtonStyle = (isActive, isDisabled) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "40px",
    height: "40px",
    padding: "0 14px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    border: "1px solid #e2e8f0",
    backgroundColor: isActive ? "#007bff" : "#ffffff",
    color: isActive ? "#ffffff" : isDisabled ? "#cbd5e1" : "#475569",
    cursor: isDisabled ? "not-allowed" : "pointer",
    boxShadow: isActive ? "0 4px 12px rgba(0, 123, 255, 0.15)" : "none",
    transition: "all 0.15s ease-in-out",
    outline: "none",
  });

  return (
    <>
      <Layout>
        <BreadCrumb />
        <div
          className="gallery-grid-area section-gap bg-image"
          style={{ backgroundColor: "#f8fafc", padding: "60px 0" }}
        >
          <div className="container">
            {/* FILTER SEARCH AREA */}
            <div className="row g-4 align-items-center mb-5">
              <div className="col-xl-9 col-lg-8 col-12">
                <div className="d-flex flex-wrap gap-2">
                  {[
                    { label: "View All", value: "view all" },
                    { label: "Specials Flyers", value: "specials flyers" },
                    {
                      label: "Informational Flyers",
                      value: "informational flyers",
                    },
                    { label: "Course Flyers", value: "course flyers" },
                    { label: "Brochures", value: "brochures" },
                    { label: "Catalogs", value: "catalogs" },
                    { label: "Tabloids", value: "tabloids" },
                    { label: "Forms", value: "forms" },
                    { label: "Videos", value: "video" },
                    { label: "Solutions", value: "solutions" },
                  ].map((btn) => (
                    <button
                      key={btn.value}
                      onClick={handleFilterChange}
                      data-filter={btn.value}
                      style={{
                        padding: "8px 18px",
                        borderRadius: "30px",
                        fontSize: "14px",
                        fontWeight: "600",
                        border: "1px solid #e2e8f0",
                        backgroundColor:
                          activeFilter === btn.value ? "#007bff" : "#ffffff",
                        color:
                          activeFilter === btn.value ? "#ffffff" : "#475569",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-xl-3 col-lg-4 col-12">
                <form
                  onSubmit={(e) => e.preventDefault()}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "12px",
                    padding: "4px 14px",
                  }}
                >
                  <input
                    ref={searchInputRef}
                    type="search"
                    style={{
                      border: "none",
                      outline: "none",
                      padding: "10px 6px",
                      fontSize: "14px",
                      width: "100%",
                      backgroundColor: "transparent",
                    }}
                    placeholder="Search materials..."
                    value={query}
                    onChange={handleSearchChange}
                  />
                  <FaSistrix style={{ color: "#64748b", fontSize: "16px" }} />
                </form>
              </div>
            </div>

            {/* MAIN CATALOG CARDS GRID */}
            <div className="row g-4">
              {items.length > 0 ? (
                items.map((item, index) => (
                  <div
                    key={item._id || index}
                    className="col-xl-3 col-lg-4 col-md-6 col-sm-12"
                  >
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "16px",
                        border: "1px solid #eef2f6",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        transition: "all 0.25s ease",
                      }}
                    >
                      {item.category === "video" ? (
                        <GalleryItemVideo data={item} />
                      ) : (
                        <GalleryItem
                          data={item}
                          onTriggerPreview={openGlobalPreview}
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center my-5 py-5">
                  <p
                    style={{
                      fontSize: "18px",
                      color: "#475569",
                      fontWeight: "700",
                    }}
                  >
                    No items match your criteria
                  </p>
                </div>
              )}
            </div>

            {/* FIXED FORMATTING PAGINATION STRIP */}
            {npage > 1 && (
              <div className="d-flex justify-content-center mt-5 pt-4">
                <nav aria-label="Material library navigation">
                  <ul
                    className="d-flex align-items-center gap-2 m-0 p-0"
                    style={{ listStyle: "none" }}
                  >
                    {/* Previous Button Arrow */}
                    <li>
                      <button
                        onClick={prePage}
                        disabled={currentPage === 1}
                        style={paginationButtonStyle(false, currentPage === 1)}
                        onMouseEnter={(e) => {
                          if (currentPage !== 1)
                            e.currentTarget.style.borderColor = "#007bff";
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== 1)
                            e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                        title="Go to Previous Page"
                      >
                        <FaChevronLeft
                          size={12}
                          style={{ marginRight: "6px" }}
                        />{" "}
                        Previous
                      </button>
                    </li>

                    {/* Numbered Core Blocks */}
                    {getPageRange(currentPage, npage).map((n, i) => {
                      const isEllipsis = typeof n !== "number";
                      return (
                        <li key={i}>
                          {isEllipsis ? (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "40px",
                                height: "40px",
                                color: "#94a3b8",
                                fontWeight: "600",
                              }}
                            >
                              {n}
                            </span>
                          ) : (
                            <button
                              onClick={() => changeCPage(n)}
                              style={paginationButtonStyle(
                                n === currentPage,
                                false
                              )}
                              onMouseEnter={(e) => {
                                if (n !== currentPage)
                                  e.currentTarget.style.borderColor = "#007bff";
                              }}
                              onMouseLeave={(e) => {
                                if (n !== currentPage)
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                              }}
                            >
                              {n}
                            </button>
                          )}
                        </li>
                      );
                    })}

                    {/* Next Button Arrow */}
                    <li>
                      <button
                        onClick={nextPage}
                        disabled={currentPage === npage}
                        style={paginationButtonStyle(
                          false,
                          currentPage === npage
                        )}
                        onMouseEnter={(e) => {
                          if (currentPage !== npage)
                            e.currentTarget.style.borderColor = "#007bff";
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== npage)
                            e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                        title="Go to Next Page"
                      >
                        Next{" "}
                        <FaChevronRight
                          size={12}
                          style={{ marginLeft: "6px" }}
                        />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </Layout>

      {/* SINGLE GLOBAL LIGHTBOX PREVIEW */}
      {lightboxUrl && (
        <FsLightbox
          toggler={lightboxToggler}
          customAttributes={[
            {
              style:
                "width: 100vw !important; height: 100vh !important; max-width: 100vw !important; max-height: 100vh !important;",
            },
          ]}
          sources={[
            <div
              style={{
                width: "90vw",
                height: "90vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              key={lightboxUrl}
            >
              <iframe
                src={lightboxUrl}
                title="Global Material Preview"
                style={{
                  border: "none",
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                }}
                allowFullScreen
              />
            </div>,
          ]}
        />
      )}
    </>
  );
};

export default Material;
